import { useEffect, useMemo, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { ChipDownContainer } from "@similarweb/ui-components";

import { useCommonProps } from "app/pages/keyword-research/keyword-analysis/pages/serp-players/useCommonProps";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { apiHelper } from "app/common/services/apiHelper";
import { ALL } from "app/common/services/categoryService";
import type { CategoryId, ICategory } from "app/common/services/categoryService.types";
import { createUniqueListOfAllDomains } from "pro-features/components/DomainsFilter/Utils";
import { CategoriesFilter } from "pro-features/components/filtersPanel/src/CategoriesFilter";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { defaultParams } from "app/Shared/defaults";

import { webSourceToDeviceMap } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import type { ITablePropsParams } from "../../../common-components/domains-table/Table";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import type {
    CategoriesChipDownRef,
    ICategoriesFilterProps,
    ICategoryFilters,
    IPageFilters,
    ITableApiParams,
} from "./Types";
import { findCategoryById } from "app/common/keyword-research/serp-players/categoryUtils";
import { tableFilterChange } from "./Utils";

const fetchService = DefaultFetchService.getInstance();

export const CategoryFilter: React.FC<ICategoriesFilterProps> = ({ categoriesAPI, activationSets }) => {
    const { params } = useCurrentStateAndParams();
    const { timeGranularity, category, funcFlag, domains } = params;
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);
    const commonProps = useCommonProps(true);

    const categoriesChipDownRef = useRef<CategoriesChipDownRef>(null);

    const [selectedCategory, setSelectedCategory] = useState<ICategory | undefined>();
    const [, setSelectedFilters] = useState<Partial<IPageFilters>>({});
    const [dataFilters, setDataFilters] = useState<ICategoryFilters>({
        Category: [],
        CustomCategory: [],
    });

    useEffect(() => {
        updateCategory(category, dataFilters.Category);
        updateCategory(category, dataFilters.CustomCategory);
    }, [category, dataFilters.CustomCategory, dataFilters.Category]);

    const updateCategory = (categoryId: CategoryId, categoryList: ICategory[]) => {
        if (categoryId && categoryList?.length > 0) {
            const selectedCategory = findCategoryById(categoryList, categoryId);
            if (selectedCategory) {
                setSelectedCategory(selectedCategory);
            }
        }
    };
    commonProps.queryParams["sourceType"] = SourceTypeMap[params.selectedPageTab];

    const defaultSort = { field: "Share", sortDirection: "asc" };

    const initialFilters = useMemo(() => {
        const keywordGroupForApi = isKeywordGroup
            ? keywordGroupsTransforms.getGroupIdFromUrlParam(params.keyword)
            : params.keyword;
        const allDomains = activationSets && createUniqueListOfAllDomains(domains?.split(","), activationSets);
        const paramsForTableApi = {
            ...(params as ITablePropsParams),
            keys: keywordGroupForApi,
            webSource: params.webSource,
            Device: webSourceToDeviceMap[params.webSource],
            timeGranularity: timeGranularity?.monthly,
            includeSubDomains: true,
            sourceType: SourceTypeMap[params.selectedPageTab],
            funcFlag,
            DomainsFilter: allDomains,
        };
        delete paramsForTableApi.keyword;
        const apiParams = apiHelper.transformParamsForAPI(paramsForTableApi) as ITableApiParams;
        apiParams.sort = defaultSort.field;
        apiParams.asc = false;
        apiParams.category = apiParams.category === defaultParams.category ? "" : apiParams.category;
        return apiParams;
    }, [params, activationSets]);

    const handleTableFilterChange = (filter: Partial<IPageFilters>) => {
        setSelectedFilters(tableFilterChange(filter));
    };
    const handleCategoriesFilterOpened = (isOpened: boolean) => {
        if (isOpened) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Category/filter opened");
        }
    };
    const onCategorySelected = (selectedCategory: ICategory) => {
        if (selectedCategory.id === ALL) {
            onCloseCategory();
        } else {
            setSelectedCategory(selectedCategory);
        }
        categoriesChipDownRef.current?.closePopup();
        const { isCustomCategory, forApi: selectedCategoryForApi } = selectedCategory;
        if (!selectedCategoryForApi) {
            return;
        }
        allTrackers.dispatchTrackEvent("Filter Table", "click", `Website Types/selected/${selectedCategoryForApi}`);

        if (isCustomCategory) {
            const category = selectedCategoryForApi;
            handleTableFilterChange({ category });
            proNavigator.updateParams({ category });
            return;
        }
        handleTableFilterChange({ category: selectedCategoryForApi });
        proNavigator.updateParams({ category: selectedCategoryForApi });
    };

    const onCloseCategory = () => {
        setSelectedCategory(undefined);
        handleTableFilterChange({ category: undefined });
        proNavigator.updateParams({ category: null });
    };

    const { data } = useQuery<ICategoryFilters>({
        queryKey: [categoriesAPI, category, funcFlag, domains, initialFilters],
        queryFn: async () => fetchService.get<ICategoryFilters>(categoriesAPI, initialFilters),
    });

    useEffect(() => {
        if (data) {
            setDataFilters(data);
        }
    }, [data]);

    return (
        <ChipDownContainer
            ref={categoriesChipDownRef}
            width={379}
            selectedText={selectedCategory?.text}
            buttonText={translate("dropdown.category")}
            closeOnItemClick={true}
            onCloseItem={onCloseCategory}
            onToggle={handleCategoriesFilterOpened}
            cssClassContainer={"Popup-content-industry-filter"}
            data-automation="keyword-category-filter-chipdown"
            noResultsMessage={translate("dropdown.no.results")}
        >
            {[
                <CategoriesFilter
                    key={dataFilters.Category?.[0]?.id}
                    categories={dataFilters.Category}
                    customCategories={dataFilters.CustomCategory}
                    onItemClickCallback={onCategorySelected}
                />,
            ]}
        </ChipDownContainer>
    );
};
